/* eslint-disable lines-between-class-members */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import {
  setupEmbeddedVideoPlayer,
  teardownEmbeddedVideoPlayer,
  getVideoSourcesFromPost,
  getVideoSourcesFromChat,
  hlsPlaylistLoader,
  hlsFragmentLoader,
} from 'mewe/utils/video-utils';
import EnvironmentUtils from 'mewe/utils/environment-utils';
import Hls from 'hls.js/dist/hls.light.js';
import { guidFor } from '@ember/object/internals';
import storage from 'mewe/shared/storage';
import { inject as service } from '@ember/service';

export default class MwVideoPlayer extends Component {
  @service inViewport;
  @service dynamicDialogs;

  @tracked isPlaying = false;
  @tracked isVideoInViewport = false;
  @tracked videoPlayPromise = null;
  @tracked sources = [];
  @tracked isVideoInitialised = false;

  elementId = `player_${guidFor(this)}`;
  setupEmbeddedVideoPlayer = setupEmbeddedVideoPlayer;
  teardownEmbeddedVideoPlayer = teardownEmbeddedVideoPlayer;
  hls = null;
  imageSize;
  videoEl;

  constructor() {
    super(...arguments);
    this.imageSize = this.args.imageSize ? this.args.imageSize : '800x800';
  }

  get videoFeedAutoPlaySetting() {
    return storage.get(storage.keys.videoFeedAutoPlaySetting);
  }

  @action
  videoOnInsert(element) {
    this.videoEl = element;
    this.videoEl.removeAttribute('controls'); // controls will be added after video sources are set

    if (!this.args.mediaDialog) {
      this.setupInViewportAutplayDetect();
    }

    this.setupEmbeddedVideoPlayer(this.videoEl);

    this.videoEl.muted = storage.get(storage.keys.unmuteVideos) !== true;

    this.videoEl.onplaying = () => {
      this.isPlaying = true;
    };
    this.videoEl.onpause = () => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      this.isPlaying = false;
    };
    this.videoEl.onvolumechange = () => {
      storage.set(storage.keys.unmuteVideos, !this.videoEl.muted);
    };
    // listening for play event from custom controls in mw-post-media component
    this.videoEl.addEventListener('play', () => {
      this.setSources();
    });
    // listening for click event on the video element to play it
    this.videoEl.addEventListener('click', () => {
      if (!this.isVideoInitialised) {
        this.setSources();
      }
    });

    if (this.args.mediaDialog) {
      if (this.args.autoplay ?? this.videoFeedAutoPlaySetting) {
        this.setSources();
      }
    }
  }

  startVideoPlay() {
    // usecase: MD is opened with video plaing inside, small chat pops up in the background
    // when receiving a new message with video, we don't want to pause the video in MD and play the one from chat
    if (this.args.isChat && this.dynamicDialogs.hasAnyDialogOpened) return;

    this.videoEl.muted = storage.get(storage.keys.unmuteVideos) !== true;

    this.videoPlayPromise = this.videoEl.play();

    if (this.videoPlayPromise) {
      this.videoPlayPromise.catch((e) => {
        if (e.code === 0) {
          return;
        }
        if (e.code === DOMException.ABORT_ERR) {
          return;
        }
        throw e;
      });
    }
  }

  // sources should be set only when video is actually to be played (either autoplay or user interaction),
  // we don't want to make video request otherwise because we count number of video requests done by user
  setSources() {
    if (this.isVideoInitialised) return;

    this.sources = this.args.isChat
      ? getVideoSourcesFromChat(this.args.media)
      : getVideoSourcesFromPost(this.args.media);

    // HLS will be used when hls source is first.
    if (this.sources[0]?.type == 'video/hls') {
      const hlsSource = this.sources[0].src;
      if (this.videoEl.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (iOS Safari, desktop Safari)
        this.videoEl.src = hlsSource;
      } else if (Hls.isSupported()) {
        // Use hls.js for browsers like Chrome/Firefox/Edge
        this.enableHlsPlayback(hlsSource);
      } else {
        // Fallback or show error
        console.warn('HLS not supported in this browser');
      }
    }

    this.isVideoInitialised = true;
    this.videoEl.setAttribute('controls', 'true'); // controls will be added after video sources are set
    this.startVideoPlay();
  }

  @action
  videoClick() {
    if (!this.isVideoInitialised) {
      this.setSources();
    } else {
      this.startVideoPlay();
    }
  }

  setupInViewportAutplayDetect() {
    const loader = document.getElementById(this.elementId);
    const viewportConfig = {
      viewportSpy: true,
      intersectionThreshold: 1,
    };
    const { onEnter, onExit } = this.inViewport.watchElement(loader, viewportConfig);

    if (this.args.autoplay ?? this.videoFeedAutoPlaySetting) {
      onEnter(this.didEnterViewport.bind(this));
    }

    onExit(this.didExitViewport.bind(this));
  }

  didEnterViewport() {
    if (this.videoEl) {
      if (this.videoEl.paused && !this.isPlaying && !document.webkitCurrentFullScreenElement) {
        this.videoEl.currentTime = 0;
        this.startVideoPlay();
      }
    }
  }

  didExitViewport() {
    if (this.videoEl) {
      this.videoEl.muted = storage.get(storage.keys.unmuteVideos) !== true;

      if (!this.videoEl.paused && this.isPlaying && !document.webkitCurrentFullScreenElement) {
        if (this.videoPlayPromise) {
          this.videoPlayPromise.then(() => {
            this.videoEl.pause();
            this.videoPlayPromise = null;
          });
        } else if (!this.videoFeedAutoPlaySetting) {
          this.videoEl.pause();
        }
      }
    }
  }

  willDestroy = () => {
    const loader = document.getElementById(this.elementId);
    this.inViewport.stopWatching(loader);
  };

  enableHlsPlayback(source) {
    if (!this.videoEl) {
      console.error('Hsl playback - missing video element');
    }

    source = source + (~source.indexOf('?') ? '&' : '?') + 'hlsGet=true';
    if (!this.hls) {
      this.hls = new Hls({
        pLoader: hlsPlaylistLoader,
        fLoader: hlsFragmentLoader,
        maxBufferLength: 20,
        maxMaxBufferLength: 30,
        maxBufferSize: 5,
        capLevelToPlayerSize: true,
      });
    }

    this.hls.loadSource(source);
    this.hls.attachMedia(this.videoEl);
  }

  get poster() {
    // args.media for attachments in chat messages.
    const media = this.args.media.photo || this.args.media.video || this.args.media;
    let imgUrl = media?._links?.img?.href;

    if (this.args.isChat) {
      imgUrl = media?._links?.thumbnail?.href;
    }

    if (!imgUrl) {
      return htmlSafe('');
    }

    imgUrl = imgUrl.replace('{imageSize}', this.imageSize).replace('{static}', '1');

    return htmlSafe(EnvironmentUtils.getImgHost(true, this.args.isPublicContent) + imgUrl);
  }

  get videoStyle() {
    return htmlSafe(this.args.style || `width: inherit; max-width: 100%`);
  }
}
